import type { Level, Tier } from '@xox/shared'
import { ChevronDown, Lock } from 'lucide-react'
import type { ReactNode } from 'react'
import { Link } from 'react-router'
import { Stars } from '../components/ui'
import { miniFill } from './art/tiles'
import type { Progress } from './progress'

/** Thumbnail of a level in the board's palette. */
export function MiniMap({
  rows,
  className = 'w-full',
}: {
  rows: readonly string[]
  /** Sizing lives here so a thumbnail can be `w-10` without fighting a baked-in width. */
  className?: string
}) {
  return (
    <div
      className={`grid aspect-square grid-cols-10 gap-px overflow-hidden rounded-md ${className}`}
      aria-hidden
    >
      {rows.flatMap((row, r) =>
        [...row].map((t, c) => (
          <span
            // biome-ignore lint/suspicious/noArrayIndexKey: positional
            key={`${r}-${c}`}
            style={{ background: miniFill(t) }}
          />
        )),
      )}
    </div>
  )
}

/** Collapsible tier heading shared by the solo level list and the race level picker. */
export function TierHeader({
  tier,
  expanded,
  onToggle,
  meta,
  locked = false,
}: {
  tier: Tier
  expanded: boolean
  onToggle: () => void
  meta: ReactNode
  locked?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={expanded}
      className="flex w-full items-center justify-between gap-3 rounded-xl px-2 py-2 text-left hover:bg-slate-200/60 dark:hover:bg-slate-800/60"
    >
      <div className="min-w-0">
        <h3 id={`tier-${tier.id}`} className="font-bold">
          <span className="text-slate-400">Tier {tier.id} · </span>
          {tier.name}
          {locked && <Lock className="ml-1.5 inline size-3.5 text-slate-400" />}
        </h3>
        <p className="truncate text-xs text-slate-500">{tier.intro}</p>
      </div>
      <div className="flex shrink-0 items-center gap-2 text-xs text-slate-500">
        <span>{meta}</span>
        <ChevronDown
          className={`size-4 transition-transform ${expanded ? 'rotate-180' : ''}`}
          aria-hidden
        />
      </div>
    </button>
  )
}

interface LevelCardProps {
  level: Level
  number: number
  unlocked: boolean
  done: Progress[string] | undefined
  /** When set the card is a button (race level picker) instead of a link into solo play. */
  onPick?: (level: Level) => void
  selected?: boolean
}

export function LevelCard({ level, number, unlocked, done, onPick, selected }: LevelCardProps) {
  const cls = [
    'flex w-full flex-col gap-1 rounded-xl bg-white p-2 text-left ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-800',
    selected ? 'ring-2 ring-sky-500 dark:ring-sky-400' : '',
  ].join(' ')
  const interactive =
    'transition-shadow hover:shadow-md focus-visible:outline-2 focus-visible:outline-sky-500'
  const inner = (
    <>
      <div className="flex items-center justify-between text-[11px] font-bold uppercase tracking-wide text-slate-500">
        <span>#{number}</span>
        {!unlocked ? (
          <Lock className="size-3.5 text-slate-400" aria-label="Locked" />
        ) : (
          <span className="font-semibold normal-case tracking-normal">par {level.par}</span>
        )}
      </div>
      {unlocked ? (
        <MiniMap rows={level.rows} />
      ) : (
        <div className="aspect-square rounded-md bg-slate-100 dark:bg-slate-800" />
      )}
      <div className="truncate text-xs font-medium">{level.name}</div>
      <div className="flex items-center">
        <Stars earned={done?.stars ?? 0} />
        {done && <span className="ml-auto text-[10px] text-slate-500">{done.bestMoves} mv</span>}
      </div>
    </>
  )
  return (
    <li>
      {onPick ? (
        <button
          type="button"
          onClick={() => onPick(level)}
          aria-pressed={selected}
          className={`${cls} ${interactive}`}
        >
          {inner}
        </button>
      ) : unlocked ? (
        <Link to={`/ice/play/${level.id}`} className={`${cls} ${interactive}`}>
          {inner}
        </Link>
      ) : (
        <div className={`${cls} opacity-60`} aria-disabled>
          {inner}
        </div>
      )}
    </li>
  )
}
